/**
 * Shopify Cart: Shipping Protection Toggle Logic (Minimal Logging)
 *
 * This script handles adding/removing a shipping protection product to the cart
 * based on a toggle button, a localStorage preference, and cart attribute.
 */
const DEBUG = false; // Set to true when debugging this script only
const log = (...args) => DEBUG && console.log('[ShippingProtection]', ...args);

// Global storage for the shipping protection product variant ID
let shippingProtectionProductId = null;

/* ========== Utility Functions ========== */

/**
 * Get the cart container element.
 */
function getMainCart() {
  const cart = document.querySelector('.main-cart');
  return cart;
}

/**
 * Get the current shipping protection preference.
 * For new visitors, set localStorage to "true" (enabled) by default.
 */
function isProtectionEnabled() {
  const value = localStorage.getItem("tg_shipping_protection");
  if (value === null) {
    localStorage.setItem("tg_shipping_protection", "true");
    return true;
  }
  return value === "true";
}

/**
 * Store the user's protection preference in localStorage.
 */
function setProtectionState(state) {
  localStorage.setItem("tg_shipping_protection", state ? "true" : "false");
}

/**
 * Sync the toggle button's visual state and data attribute.
 */
function toggleButtonState(button, active) {
  if (!button) return;
  if (active) {
    button.classList.add('active');
  } else {
    button.classList.remove('active');
  }
  button.setAttribute('data-protection-enabled', active ? 'true' : 'false');
}

/**
 * Find the shipping protection item in the cart.
 * Returns both the item and its index in the cart array.
 */
function findShippingItem(cart, productId) {
  let item = null;
  let index = -1;
  cart.items.forEach((cartItem, i) => {
    if (String(cartItem.variant_id) === String(productId)) {
      item = cartItem;
      index = i;
    }
  });
  return { item, index };
}

/* ========== Cart Operations ========== */

/**
 * Update the cart to include or exclude shipping protection.
 * - Only add protection if other cart items exist.
 * - If protection is the only item, remove it (but keep the enabled state).
 */
async function updateProtection(state, productId) {
  const mainCart = getMainCart();
  if (mainCart) mainCart.classList.add('disabled');
  log('[updateProtection] state:', state, 'productId:', productId);

  try {
    // Update cart attribute to reflect protection choice.
    const attrUpdate = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: { "Include Shipping Protection": state ? "True" : "False" }
      })
    });
    if (!attrUpdate.ok) throw new Error("Failed to update cart attributes");

    // Fetch latest cart state.
    const cartRes = await fetch('/cart.js');
    if (!cartRes.ok) throw new Error("Failed to fetch cart");
    const cart = await cartRes.json();

    // Find shipping protection item.
    const { item, index } = findShippingItem(cart, productId);

    if (state) {
      const nonProtectionItems = cart.items.filter(i => String(i.variant_id) !== String(productId));
      if (nonProtectionItems.length === 0) {
        if (item) await updateLineItem(index + 1, 0, "Removing protection as it's the only item");
        return;
      }
      if (item) {
        if (item.quantity !== 1) await updateLineItem(index + 1, 1, "Forcing protection quantity to 1");
      } else {
        await addProtectionToCart(productId);
      }
    } else {
      if (item) await updateLineItem(index + 1, 0, "Removing protection");
    }
  } catch (err) {
    console.error("[updateProtection] Error:", err);
  } finally {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    if (mainCart) {
      mainCart.classList.remove('disabled');
      log('[updateProtection] Finished updating protection.');
    }
  }
}

/**
 * Change the quantity of a cart line item.
 */
async function updateLineItem(line, quantity, logMsg) {
  log('[updateLineItem]', logMsg, 'Line:', line, 'Qty:', quantity);
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ line, quantity })
  });
  if (!res.ok) throw new Error(`Failed to change line ${line} to qty ${quantity}`);
}

/**
 * Add the shipping protection product to the cart.
 */
async function addProtectionToCart(productId) {
  log("[addProtectionToCart] Adding protection for productId:", productId);
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: productId, quantity: 1 })
  });
  if (!res.ok) throw new Error("Failed to add protection product");
}

/* ========== UI Initialization & Event Handlers ========== */

/**
 * Initialize the toggle button state.
 * If syncFromAttribute is true, read the server-rendered state.
 */
function initializeShippingButton({ syncFromAttribute = false } = {}) {
  const cart = getMainCart();
  if (cart) {
    // Read the shipping protection product variant ID from the cart element.
    shippingProtectionProductId = cart.getAttribute('data-shipping-protection');
    log('[initializeShippingButton] Variant ID:', shippingProtectionProductId);
    const btn = cart.querySelector('.shipping-check');
    if (btn) {
      if (syncFromAttribute) {
        const serverEnabled = btn.getAttribute('data-protection-enabled') === 'true';
        setProtectionState(serverEnabled);
        toggleButtonState(btn, serverEnabled);
      } else {
        toggleButtonState(btn, isProtectionEnabled());
      }
    }
  }
}

/**
 * Handle toggle button click to toggle shipping protection.
 */
function handleShippingToggle(event) {
  if (event.target.closest('.protection-info')) return;
  const btn = event.target.closest('.shipping-check');
  if (!btn) return;
  const cart = getMainCart();
  if (cart) {
    shippingProtectionProductId = cart.getAttribute('data-shipping-protection');
  }
  const newState = !isProtectionEnabled();
  setProtectionState(newState);
  toggleButtonState(btn, newState);
  updateProtection(newState, shippingProtectionProductId);
}

/* ========== Event Listeners ========== */

document.addEventListener('DOMContentLoaded', () => {
  const cart = getMainCart();
  if (!cart) return;

  cart.addEventListener('click', handleShippingToggle);
  initializeShippingButton({ syncFromAttribute: true });

  if (isProtectionEnabled() && shippingProtectionProductId) {
    updateProtection(true, shippingProtectionProductId);
  }

  document.addEventListener('on:cart:add', (event) => {
    if (isProtectionEnabled() && shippingProtectionProductId) {
      updateProtection(true, shippingProtectionProductId);
    }
  });
  document.addEventListener('on:line-item:change:end', (event) => {
    if (isProtectionEnabled() && shippingProtectionProductId) {
      updateProtection(true, shippingProtectionProductId);
    }
  });
});